'use client';

import { ThreadPrimitive, ComposerPrimitive, AssistantIf, useAuiState, useAui } from '@assistant-ui/react';
import { telemetryIncrementAction } from 'core-events/actions';
import { useSearchParams } from 'next/navigation';
import { type JSX, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, Tooltip } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useHighlightTimer } from '../../_hooks';
import { UsageLimitMessage } from '../browse-limit';
import { AiDisclosure } from './AiDisclosure';
import { AssistantMessage } from './AssistantMessage';
import { RateLimitMessage } from './RateLimitMessage';
import { ThreadWelcome } from './ThreadWelcome';
import { UserMessage } from './UserMessage';
import { FeedbackWidget } from './feedback';

/**
 * Scroll to bottom button.
 */
function ThreadScrollToBottom(): JSX.Element {
    return (
        <ThreadPrimitive.ScrollToBottom asChild>
            <button
                type="button"
                className="absolute -top-10 left-1/2 -translate-x-1/2 z-10 rounded-full p-2 bg-white shadow-md border border-gray-200 disabled:invisible hover:bg-gray-50 transition-colors"
                aria-label="Scroll to bottom"
            >
                <Icon name="arrowDown" className="h-4 w-4 text-gray-600" />
            </button>
        </ThreadPrimitive.ScrollToBottom>
    );
}

export const MAX_CHAR_LIMIT = 2_000;
export const COUNTER_VISIBLE_THRESHOLD = 0.5;
export const NEAR_LIMIT_THRESHOLD = 0.9;

const COMPOSER_PLACEHOLDER = "Describe the scene you're looking for";

// Duration the attention-grabbing purple highlight stays on an empty composer
// before fading back to its resting border.
export const HIGHLIGHT_DURATION_MS = 5_000;

const HIGHLIGHT_GLOW_GRADIENT = 'linear-gradient(to bottom right, #9952E0, #CC5CE3, #FF66E6)';

/**
 * Main composer for sending new messages.
 */
function ThreadComposer(): JSX.Element {
    const params = useSearchParams();
    const promptParamText = params.get('prompt');
    const hasSetInitialText = useRef(false);
    const aui = useAui();
    const { isRateLimited, rateLimitResetsAt, hasReachedLimit } = useChatSessionContext();

    // on mount, set initial composer text from query param,
    // start chat. run only once
    useEffect(() => {
        if (promptParamText && !hasSetInitialText.current) {
            const normalizedPrompt = promptParamText.trim();
            if (normalizedPrompt.length === 0) {
                return;
            }

            const clampedPrompt = normalizedPrompt.slice(0, MAX_CHAR_LIMIT);

            hasSetInitialText.current = true;
            aui.composer().setText(clampedPrompt);

            if (!isRateLimited && !hasReachedLimit) {
                aui.composer().send();
                // manually dispatch telemetry event, as we bypass the root onSubmit handler with this
                void telemetryIncrementAction('conv-search.chat.message');
            }
        }
    }, [promptParamText, aui, isRateLimited, hasReachedLimit]);

    const text = useAuiState(({ composer }) => composer.text);
    const isEmpty = useAuiState(({ thread }) => thread.isEmpty);
    const showHighlight = useHighlightTimer(isEmpty, HIGHLIGHT_DURATION_MS);
    const sendLabel = isEmpty ? 'Search' : 'Refine';
    const charCount = text.length;
    const isAtLimit = charCount >= MAX_CHAR_LIMIT;
    const isNearLimit = charCount >= MAX_CHAR_LIMIT * NEAR_LIMIT_THRESHOLD;
    const showCounter = charCount >= MAX_CHAR_LIMIT * COUNTER_VISIBLE_THRESHOLD;
    const isSubmitDisabled = isAtLimit || isRateLimited || hasReachedLimit;

    return (
        <ComposerPrimitive.Root
            className="relative flex w-full flex-col"
            onSubmit={() => {
                void telemetryIncrementAction('conv-search.chat.message');
            }}
        >
            {isRateLimited && rateLimitResetsAt && <RateLimitMessage resetsAt={rateLimitResetsAt} />}
            <div className="relative">
                <div
                    aria-hidden
                    data-testid="composer-highlight-glow"
                    className={twMerge(
                        'pointer-events-none absolute -inset-0.5 rounded-xl opacity-0 transition-opacity duration-700',
                        showHighlight && 'opacity-100',
                    )}
                    style={{ backgroundImage: HIGHLIGHT_GLOW_GRADIENT, filter: 'blur(6px)' }}
                />
                <div
                    className={twMerge(
                        'relative flex w-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-700 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100',
                        showHighlight && 'border-[#9952E0] focus-within:border-[#9952E0] focus-within:ring-0',
                        hasReachedLimit && 'rounded-t-none',
                    )}
                >
                    <ComposerPrimitive.Input
                        placeholder={COMPOSER_PLACEHOLDER}
                        className="min-h-[80px] max-h-[200px] w-full resize-none overflow-y-auto bg-transparent px-3 py-2 text-sm outline-none placeholder:text-gray-400"
                        rows={3}
                        maxLength={MAX_CHAR_LIMIT}
                        autoFocus={!hasReachedLimit}
                        disabled={hasReachedLimit}
                        aria-label="Message input"
                    />
                    {showCounter && (
                        <div className="flex justify-end px-3 pb-1">
                            <span
                                className={twMerge(
                                    'text-xs px-1.5 py-0.5 rounded border',
                                    isNearLimit ? 'text-red-600 border-red-300' : 'text-gray-500 border-gray-300',
                                )}
                                aria-label="Character count"
                            >
                                {charCount.toLocaleString()} / {MAX_CHAR_LIMIT.toLocaleString()}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center justify-end px-2 pb-2">
                        <AssistantIf condition={({ thread }) => !thread.isRunning}>
                            <ComposerPrimitive.Send asChild>
                                <button
                                    data-testid="conv-search-submit"
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    className={twMerge(
                                        'px-4 py-1.5 text-sm rounded-sm transition-colors cursor-pointer',
                                        isSubmitDisabled
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-800 text-white hover:bg-gray-700',
                                    )}
                                >
                                    {sendLabel}
                                </button>
                            </ComposerPrimitive.Send>
                        </AssistantIf>
                        <AssistantIf condition={({ thread }) => thread.isRunning}>
                            <Tooltip
                                title="Stop"
                                className="py-1.5"
                                variant="dark"
                                trigger={
                                    <ComposerPrimitive.Cancel asChild>
                                        <button
                                            type="button"
                                            aria-label="Stop"
                                            className="px-2 py-2 rounded-full bg-gray-200 hover:bg-gray-400 transition flex items-center gap-1 cursor-pointer"
                                        >
                                            <Icon name="controlsStop" className="h-3 w-3 fill-current" />
                                        </button>
                                    </ComposerPrimitive.Cancel>
                                }
                            />
                        </AssistantIf>
                    </div>
                </div>
            </div>
            <AiDisclosure />
        </ComposerPrimitive.Root>
    );
}

/**
 * Main chat thread component using assistant-ui primitives.
 * Renders the chat interface with user messages, assistant messages, and composer.
 */
export function ChatThread(): JSX.Element {
    const { hasReachedLimit } = useChatSessionContext();

    return (
        <ThreadPrimitive.Root className="flex h-full flex-col bg-white">
            <ThreadPrimitive.Viewport className="relative flex flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth">
                {!hasReachedLimit && (
                    <AssistantIf condition={({ thread }) => thread.isEmpty}>
                        <ThreadWelcome />
                    </AssistantIf>
                )}

                <div className="px-4 pt-2 flex-1">
                    <ThreadPrimitive.Messages
                        components={{
                            UserMessage,
                            AssistantMessage,
                        }}
                    />
                    <FeedbackWidget />
                </div>

                <ThreadPrimitive.ViewportFooter className="sticky bottom-0 mt-auto flex w-full flex-col bg-white">
                    <ThreadScrollToBottom />
                    <div className="p-4">
                        {hasReachedLimit && <UsageLimitMessage />}
                        <ThreadComposer />
                    </div>
                </ThreadPrimitive.ViewportFooter>
            </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
    );
}
